/**
 * Thinking Engine — the reasoning pipeline.
 *
 * An ordered list of pure reasoning steps mirroring how a senior designer
 * thinks: understand the business and goal, choose a communication and creative
 * direction, commit to a visual language (genome), then reason each design
 * strategy — always deriving from the genome (language), research (industry
 * intelligence), and memory (diversity), and always explaining WHY.
 *
 * Each step reads the shared context (inputs + decisions made so far) and
 * returns a { choice, why, basis } decision. No pixels, geometry, or objects.
 */

import { decision, humanize, firstReasons, list, union, variationNote } from './reasoning';
import { HIERARCHY_ROLES } from './thinkingSchema';

const OFFER_ROLES = HIERARCHY_ROLES.offerLed;
const MESSAGE_ROLES = HIERARCHY_ROLES.messageLed;

const CONTENT_PRIORITY_OFFER = ['hero', 'cta', 'pricing', 'offer', 'features', 'stats', 'gallery', 'testimonial', 'contact', 'header', 'footer'];
const CONTENT_PRIORITY_MESSAGE = ['hero', 'features', 'stats', 'gallery', 'pricing', 'testimonial', 'cta', 'contact', 'header', 'footer'];

const NARRATIVE_BY_COMPOSITION = {
  diagonal: 'a diagonal sweep that pulls the eye from the focal point toward the call to action',
  asymmetric: 'a dynamic off-axis flow that creates energy and movement',
  'centered-axis': 'a calm central gaze that settles on one hero moment',
  symmetric: 'a balanced, orderly read top to bottom',
  'editorial-columns': 'a magazine-like column read that unfolds the story',
  'modular-grid': 'a structured module-by-module scan',
  'layered-overlap': 'depth through overlap that layers meaning front to back',
  'bento-grid': 'a compartmentalised scan across tiles of equal invitation',
  freeform: 'a playful, exploratory path across the canvas',
};

export const STEPS = [
  {
    id: 'understand-business', key: 'businessIntent',
    run: (ctx) => {
      const who = ctx.brief.industry ? humanize(ctx.brief.industry) : 'the business';
      const cat = ctx.brief.category ? ` (${humanize(ctx.brief.category)})` : '';
      const pos = firstReasons(ctx.research.brandPositioning, 2) || 'clear and trustworthy';
      return decision(
        `Position ${who}${cat} as ${pos}`,
        `The brief is a ${ctx.brief.documentType || 'template'} for ${who}${cat}. ${ctx.research.industryUnderstanding || ctx.research.marketUnderstanding || 'The design must communicate its core message instantly.'}`,
        ['brief:industry', 'research:industryUnderstanding'],
      );
    },
  },
  {
    id: 'understand-marketing-goal', key: 'marketingIntent',
    run: (ctx) => {
      const goal = ctx.brief.purpose ? humanize(ctx.brief.purpose) : 'awareness';
      const psy = firstReasons(ctx.research.marketingPsychology, 2) || 'lead with the benefit and drive one clear action';
      return decision(
        `Drive ${goal}`,
        `The campaign goal is ${goal}. The design should ${psy}.`,
        ['brief:purpose', 'research:marketingPsychology'],
      );
    },
  },
  {
    id: 'understand-audience', key: 'communicationStrategy',
    run: (ctx) => {
      const aud = ctx.brief.audience ? humanize(ctx.brief.audience) : 'a broad audience';
      return decision(
        `Speak to ${aud}`,
        `${ctx.research.audienceUnderstanding || `Communicate clearly to ${aud}.`} ${firstReasons(ctx.research.marketingPsychology, 1) || 'Keep the message effortless to read'}.`,
        ['brief:audience', 'research:audienceUnderstanding'],
      );
    },
  },
  {
    id: 'choose-creative-direction', key: 'creativeDirection',
    run: (ctx) => {
      const feel = ctx.brief.mood ? humanize(ctx.brief.mood) : ctx.genome.family;
      const pos = firstReasons(ctx.research.brandPositioning, 1) || 'a distinctive, confident brand feel';
      return decision(
        `A ${ctx.genome.visualPhilosophy} direction that feels ${feel}`,
        `Lead with ${ctx.genome.visualPhilosophy} as the emotional core — it reads as ${feel} and reinforces ${pos}, which is what differentiates this design in its market.`,
        ['genome:visualPhilosophy', 'brief:mood', 'research:brandPositioning'],
      );
    },
  },
  {
    id: 'choose-visual-language', key: 'visualLanguage',
    run: (ctx) => decision(
      `${ctx.genome.name} — ${humanize(ctx.genome.composition)} composition, ${humanize(ctx.genome.typography)} type, ${humanize(ctx.genome.color)} colour`,
      `The ${ctx.genome.name} genome defines the whole visual language: ${humanize(ctx.genome.composition)} composition, ${humanize(ctx.genome.typography)} typography, ${humanize(ctx.genome.color)} colour, and ${humanize(ctx.genome.shape)} shapes — chosen so every element speaks one language.`,
      ['genome'],
    ),
  },
  {
    id: 'choose-style-genome', key: 'styleGenome',
    run: (ctx) => decision(
      ctx.genome.id,
      `${ctx.genome.name} is the chosen language because research recommends it for this brief and it expresses ${ctx.genome.visualPhilosophy}.${ctx.genomeSwapped ? ` Memory flagged the preferred genome as over-used, so we switched to this distinct, research-backed language for diversity.` : ''}`,
      ['genome', 'research:recommendedGenomes', ...(ctx.genomeSwapped ? ['memory:avoid:genome'] : [])],
    ),
  },
  {
    id: 'choose-composition-strategy', key: 'compositionStrategy',
    run: (ctx) => decision(
      ctx.genome.composition,
      `Use a ${humanize(ctx.genome.composition)} composition on a ${humanize(ctx.genome.grid)} — native to the ${ctx.genome.name} language and matched to a ${ctx.genome.rhythm} rhythm.${variationNote('composition', ctx.variationAxes)}`,
      ['genome:composition', 'genome:grid', 'genome:rhythm'],
    ),
  },
  {
    id: 'choose-hierarchy-strategy', key: 'hierarchyStrategy',
    run: (ctx) => {
      const roles = ctx.offerLed ? OFFER_ROLES : MESSAGE_ROLES;
      return decision(
        roles,
        `Rank ${roles.join(' → ')}. ${ctx.offerLed ? 'The offer leads because this is a promotion-driven campaign' : 'The headline leads to land the message first'}, and the ${humanize(ctx.genome.hero)} hero sets the single focal point.`,
        ['brief:purpose', 'genome:hero'],
      );
    },
  },
  {
    id: 'choose-visual-narrative', key: 'visualNarrative',
    run: (ctx) => {
      const comp = ctx.decisions.compositionStrategy.choice;
      const flow = NARRATIVE_BY_COMPOSITION[comp] || 'a deliberate path from the focal point to the call to action';
      const order = ctx.decisions.hierarchyStrategy.choice;
      return decision(
        `Guide the eye via ${flow}`,
        `The story unfolds as ${flow}; the ${humanize(comp)} composition and the ${list(order).join(' → ')} hierarchy work together so the viewer absorbs the message then acts.`,
        ['genome:composition', 'plan:hierarchyStrategy'],
      );
    },
  },
  {
    id: 'choose-typography-strategy', key: 'typographyStrategy',
    run: (ctx) => decision(
      ctx.genome.typography,
      `${humanize(ctx.genome.typography)} type — the ${ctx.genome.name} language calls for it${firstReasons(ctx.research.typographyDirection, 1) ? `, and research favours ${firstReasons(ctx.research.typographyDirection, 1)}` : ''}. Establish a ${list(ctx.genome.premiumCues).includes('refined-type') ? 'refined' : 'clear'} hierarchy with at most two families.${variationNote('typography', ctx.variationAxes)}`,
      ['genome:typography', 'research:typographyDirection'],
    ),
  },
  {
    id: 'choose-color-strategy', key: 'colorStrategy',
    run: (ctx) => decision(
      ctx.genome.color,
      `A ${humanize(ctx.genome.color)} palette. ${firstReasons(ctx.research.colorPsychology, 2) ? `Research shows ${firstReasons(ctx.research.colorPsychology, 2)}` : 'Contrast drives focus and a limited palette reads premium'}, and the ${ctx.genome.name} genome reinforces ${ctx.genome.visualPhilosophy}.${variationNote('color', ctx.variationAxes)}`,
      ['genome:color', 'research:colorPsychology'],
    ),
  },
  {
    id: 'choose-photography-strategy', key: 'photographyStrategy',
    run: (ctx) => decision(
      ctx.genome.photography,
      ctx.genome.photography === 'none'
        ? `No photography — the ${ctx.genome.name} language leans on type, shape, and colour instead.`
        : `Use ${humanize(ctx.genome.photography)} imagery${firstReasons(ctx.research.photographyDirection, 1) ? `; research favours ${firstReasons(ctx.research.photographyDirection, 1)}` : ''}, treated to match the genome's mood.`,
      ['genome:photography', 'research:photographyDirection'],
    ),
  },
  {
    id: 'choose-illustration-strategy', key: 'illustrationStrategy',
    run: (ctx) => decision(
      ctx.genome.illustration,
      ctx.genome.illustration === 'none'
        ? `Skip illustration — it would dilute the ${ctx.genome.name} language here.`
        : `Use ${humanize(ctx.genome.illustration)} illustration, consistent with the ${ctx.genome.name} language and purposeful rather than decorative.`,
      ['genome:illustration'],
    ),
  },
  {
    id: 'choose-icon-strategy', key: 'iconStrategy',
    run: (ctx) => decision(
      ctx.genome.icon,
      ctx.genome.icon === 'none'
        ? `No icons — the composition communicates without them.`
        : `Use a single ${humanize(ctx.genome.icon)} icon set so iconography stays consistent and on-language.`,
      ['genome:icon'],
    ),
  },
  {
    id: 'choose-shape-language', key: 'shapeStrategy',
    run: (ctx) => decision(
      ctx.genome.shape,
      `Adopt a ${humanize(ctx.genome.shape)} shape language with ${humanize(ctx.genome.border)} borders — it carries the ${ctx.genome.name} personality across cards, badges, and dividers.`,
      ['genome:shape', 'genome:border'],
    ),
  },
  {
    id: 'choose-decoration-strategy', key: 'decorationStrategy',
    run: (ctx) => decision(
      ctx.genome.decoration,
      ctx.genome.decoration === 'none'
        ? `No decoration — restraint is part of the ${ctx.genome.name} premium feel.`
        : `Apply ${humanize(ctx.genome.decoration)} decoration sparingly, kept off the content so it enriches rather than clutters.`,
      ['genome:decoration'],
    ),
  },
  {
    id: 'choose-cta-strategy', key: 'ctaStrategy',
    run: (ctx) => decision(
      ctx.genome.cta,
      `A ${humanize(ctx.genome.cta)} call to action. ${firstReasons(ctx.research.ctaBehaviour, 1) ? `Research favours ${firstReasons(ctx.research.ctaBehaviour, 1)}; ` : ''}one primary action, high contrast, placed at the end of the reading flow.`,
      ['genome:cta', 'research:ctaBehaviour'],
    ),
  },
  {
    id: 'choose-content-priorities', key: 'contentPriorities',
    run: (ctx) => {
      const priority = ctx.offerLed ? CONTENT_PRIORITY_OFFER : CONTENT_PRIORITY_MESSAGE;
      const rank = (fam) => { const i = priority.indexOf(fam); return i === -1 ? priority.length : i; };
      const order = [...list(ctx.genome.componentFamilies)].sort((a, b) => rank(a) - rank(b));
      return decision(
        order,
        `Prioritise ${order.join(', ')} — ${ctx.offerLed ? 'the offer and CTA carry a promotion' : 'the hero and message lead'}, drawn from the ${ctx.genome.name} preferred blocks and ordered by the campaign goal.`,
        ['genome:componentFamilies', 'brief:purpose'],
      );
    },
  },
  {
    id: 'choose-premium-cues', key: 'premiumCues',
    run: (ctx) => {
      const cues = union(ctx.genome.premiumCues, ctx.research.premiumCues);
      const tier = ctx.brief.premiumLevel ? ` for a ${humanize(ctx.brief.premiumLevel)} tier` : '';
      return decision(
        cues,
        `Signal premium through ${cues.map(humanize).join(', ')} — combining the ${ctx.genome.name} cues with industry research${tier}.`,
        ['genome:premiumCues', 'research:premiumCues', 'brief:premiumLevel'],
      );
    },
  },
  {
    id: 'apply-research-and-avoid', key: 'avoidRules',
    run: (ctx) => {
      const memoryRules = list(ctx.constraints.avoidRules);
      const rules = union(ctx.genome.avoid, ctx.research.thingsToAvoid, memoryRules);
      return decision(
        rules,
        `Avoid ${rules.map(humanize).join(', ')}. The ${ctx.genome.name} genome and industry research warn against these${memoryRules.length ? `, and memory adds ${memoryRules.length} diversity rule(s)` : ''}.`,
        ['genome:avoid', 'research:thingsToAvoid', 'memory:avoidRules'],
      );
    },
  },
  {
    id: 'apply-memory-variation', key: 'variationAxes',
    run: (ctx) => {
      const axes = ctx.variationAxes;
      return decision(
        axes,
        axes.length
          ? `Deliberately diverge on ${axes.join(', ')} — memory shows these axes are over-used across recent designs, so this concept must differ there.`
          : `No prior designs to diverge from — full creative freedom on every axis.`,
        ['memory:recommendedVariationAxes'],
      );
    },
  },
];
